import React from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Users, 
  Briefcase, 
  GraduationCap, 
  Home,
  Baby,
  Heart as HealthIcon,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { PlayerStats, PlayerStatus } from '../types/game';

interface CharacterAvatarProps {
  currentAge: number;
  stats: PlayerStats;
  status: PlayerStatus;
  currentStage: string;
  recentEvents: string[];
  className?: string;
}

const CharacterAvatar: React.FC<CharacterAvatarProps> = ({
  currentAge,
  stats,
  status,
  currentStage,
  recentEvents,
  className = ""
}) => {
  // Determine character appearance based on age
  const getAgeAppearance = (age: number) => {
    if (age < 18) return { emoji: '👶', description: 'Child' };
    if (age < 25) return { emoji: '🧑‍🎓', description: 'Young Adult' };
    if (age < 35) return { emoji: '👨‍💼', description: 'Adult' };
    if (age < 50) return { emoji: '👨‍💻', description: 'Middle-aged' };
    if (age < 65) return { emoji: '👨‍🏫', description: 'Mature Adult' };
    if (age < 80) return { emoji: '👴', description: 'Senior' };
    return { emoji: '👴', description: 'Elderly' };
  };

  // Determine health appearance
  const getHealthAppearance = (health: number) => {
    if (health >= 80) return { color: 'text-green-400', bg: 'bg-green-500/20', status: 'Excellent' };
    if (health >= 60) return { color: 'text-yellow-400', bg: 'bg-yellow-500/20', status: 'Good' };
    if (health >= 40) return { color: 'text-orange-400', bg: 'bg-orange-500/20', status: 'Fair' };
    if (health >= 20) return { color: 'text-red-400', bg: 'bg-red-500/20', status: 'Poor' };
    return { color: 'text-red-600', bg: 'bg-red-600/20', status: 'Critical' };
  };

  // Determine lifestyle appearance
  const getLifestyleAppearance = (stats: PlayerStats) => {
    const isAthletic = stats.health >= 70;
    const isOverworked = stats.stress >= 70;
    const isWealthy = stats.wealth >= 70;
    
    if (isOverworked) return { modifier: '😴', description: 'Overworked' };
    if (isAthletic) return { modifier: '💪', description: 'Athletic' };
    if (isWealthy) return { modifier: '💎', description: 'Wealthy' };
    return { modifier: '', description: 'Normal' };
  };

  // Check for recent events that affect appearance
  const getEventModifiers = (events: string[]) => {
    const modifiers: { emoji: string; description: string }[] = [];
    
    if (events.some(e => e.includes('accident') || e.includes('injury'))) {
      modifiers.push({ emoji: '🩹', description: 'Injured' });
    }
    if (events.some(e => e.includes('sick') || e.includes('illness'))) {
      modifiers.push({ emoji: '🤒', description: 'Sick' });
    }
    if (events.some(e => e.includes('death') || e.includes('funeral'))) {
      modifiers.push({ emoji: '😢', description: 'Grieving' });
    }
    if (events.some(e => e.includes('marriage') || e.includes('wedding'))) {
      modifiers.push({ emoji: '💍', description: 'Married' });
    }
    if (events.some(e => e.includes('birth') || e.includes('baby'))) {
      modifiers.push({ emoji: '👶', description: 'New Parent' });
    }
    
    return modifiers;
  };

  const ageAppearance = getAgeAppearance(currentAge);
  const healthAppearance = getHealthAppearance(stats.health);
  const lifestyleAppearance = getLifestyleAppearance(stats);
  const eventModifiers = getEventModifiers(recentEvents);

  // Determine family status
  const getFamilyStatus = () => {
    if (status.hasSpouse && status.hasChildren) {
      return { emoji: '👨‍👩‍👧‍👦', description: 'Family with Children' };
    }
    if (status.hasSpouse) {
      return { emoji: '👫', description: 'Married' };
    }
    if (status.hasChildren) {
      return { emoji: '👨‍👧‍👦', description: 'Single Parent' };
    }
    return { emoji: '👤', description: 'Single' };
  };

  const familyStatus = getFamilyStatus();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`bg-slate-800/50 rounded-2xl border border-slate-700 p-6 ${className}`}
    >
      {/* Character Display */}
      <div className="text-center mb-6">
        <motion.div
          key={`${currentAge}-${stats.health}-${eventModifiers.length}`}
          initial={{ scale: 0.8, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="relative inline-block"
        >
          {/* Main Character */}
          <div className="text-8xl mb-2 relative">
            {ageAppearance.emoji}
            {lifestyleAppearance.modifier && (
              <span className="absolute -top-2 -right-2 text-2xl">
                {lifestyleAppearance.modifier}
              </span>
            )}
          </div>
          
          {/* Event Modifiers */}
          {eventModifiers.map((modifier, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              className="absolute -top-1 -right-1 text-2xl"
            >
              {modifier.emoji}
            </motion.div>
          ))}
        </motion.div>

        {/* Character Info */}
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-white">
            {ageAppearance.description} • Age {currentAge}
          </h3>
          <p className="text-slate-400 text-sm">
            {familyStatus.description}
          </p>
          {lifestyleAppearance.description !== 'Normal' && (
            <p className="text-blue-400 text-sm">
              {lifestyleAppearance.description}
            </p>
          )}
        </div>
      </div>

      {/* Status Indicators */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Health Status */}
        <div className={`p-3 rounded-xl border ${healthAppearance.bg} border-slate-600`}>
          <div className="flex items-center space-x-2 mb-2">
            <HealthIcon size={16} className={healthAppearance.color} />
            <span className="text-sm font-medium text-white">Health</span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-lg font-bold ${healthAppearance.color}`}>
              {stats.health}%
            </span>
            <span className={`text-xs ${healthAppearance.color}`}>
              {healthAppearance.status}
            </span>
          </div>
        </div>

        {/* Family Status */}
        <div className="p-3 rounded-xl border border-slate-600 bg-slate-700/50">
          <div className="flex items-center space-x-2 mb-2">
            <Users size={16} className="text-purple-400" />
            <span className="text-sm font-medium text-white">Family</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-2xl">{familyStatus.emoji}</span>
            <span className="text-xs text-slate-400">
              {familyStatus.description}
            </span>
          </div>
        </div>
      </div>

      {/* Life Stage Progress */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-white mb-3">Life Stage</h4>
        <div className="flex items-center space-x-2">
          <div className="flex-1 bg-slate-700 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(currentAge / 100) * 100}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
            />
          </div>
          <span className="text-xs text-slate-400">{currentStage}</span>
        </div>
      </div>

      {/* Recent Events */}
      {eventModifiers.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-white mb-3">Recent Events</h4>
          <div className="space-y-2">
            {eventModifiers.map((modifier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-2 p-2 bg-slate-700/50 rounded-lg"
              >
                <span className="text-lg">{modifier.emoji}</span>
                <span className="text-sm text-slate-300">{modifier.description}</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Life Milestones */}
      <div className="mt-6 pt-4 border-t border-slate-700">
        <h4 className="text-sm font-medium text-white mb-3">Life Milestones</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center space-x-2">
            <GraduationCap size={12} className="text-blue-400" />
            <span className="text-slate-400">Education: {stats.education}%</span>
          </div>
          <div className="flex items-center space-x-2">
            <Briefcase size={12} className="text-green-400" />
            <span className="text-slate-400">Career: {stats.career}%</span>
          </div>
          <div className="flex items-center space-x-2">
            <Heart size={12} className="text-red-400" />
            <span className="text-slate-400">Happiness: {stats.happiness}%</span>
          </div>
          <div className="flex items-center space-x-2">
            <Home size={12} className="text-yellow-400" />
            <span className="text-slate-400">Wealth: {stats.wealth}%</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CharacterAvatar;
